/**
 * 在庫閾値設定モーダル
 *
 * `InventoryPage` から切り出した。切り出しの理由は2点。
 * - 発注点閾値は最低在庫閾値との交差検証を持ち、単体でテストできる形にするため
 * - フォームをモーダル表示中だけマウントし、非表示中に Form インスタンスへ
 *   アクセスしないようにするため（未接続の Form インスタンス操作を避ける）
 */
import { Button, Form, InputNumber, Modal, Space } from 'antd';
import { useIsFormSubmittable } from '@/shared/hooks/useIsFormSubmittable';

/** 在庫閾値(最低在庫・発注点)の最小値(0以上の整数) */
const INVENTORY_THRESHOLD_MIN = 0;

/** 閾値設定フォームの入力値 */
export interface InventoryThresholdFormValues {
  readonly minimumThreshold: number;
  readonly reorderThreshold: number;
}

interface Props {
  readonly open: boolean;
  readonly title: string;
  readonly isSubmitting: boolean;
  readonly initialValues: InventoryThresholdFormValues;
  readonly onSubmit: (values: InventoryThresholdFormValues) => Promise<void>;
  readonly onCancel: () => void;
}

export function InventoryThresholdModal({
  open,
  title,
  isSubmitting,
  initialValues,
  onSubmit,
  onCancel,
}: Props): React.ReactElement {
  const [form] = Form.useForm<InventoryThresholdFormValues>();
  const isSubmittable = useIsFormSubmittable({ form, isFormVisible: open });

  const handleFinish = async (values: InventoryThresholdFormValues): Promise<void> => {
    await onSubmit(values);
  };

  /**
   * 最低在庫閾値の変更時に発注点閾値を再検証する。
   *
   * 発注点閾値の交差検証は最低在庫閾値に依存するが、antd は「一度検証済みの
   * フィールド」しか `dependencies` で再検証しない。プリフィルされたまま
   * 未検証の発注点閾値はエラーが表示されず、送信ボタンだけが無効になって
   * 理由が分からない状態になるため、明示的に再検証する。
   */
  const handleValuesChange = (changedValues: Partial<InventoryThresholdFormValues>): void => {
    if (!('minimumThreshold' in changedValues)) {
      return;
    }
    void form.validateFields(['reorderThreshold']).catch(() => undefined);
  };

  return (
    <Modal title={title} open={open} onCancel={onCancel} footer={null} destroyOnHidden>
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onFinish={handleFinish}
        onValuesChange={handleValuesChange}
      >
        <Form.Item
          name="minimumThreshold"
          label="最低在庫閾値"
          rules={[
            { required: true, message: '最低在庫閾値を入力してください' },
            {
              type: 'integer',
              min: INVENTORY_THRESHOLD_MIN,
              message: '0以上の整数を入力してください',
            },
          ]}
          extra="この値を下回ると「在庫不足」アラートが表示されます"
        >
          <InputNumber className="w-full" placeholder="最低在庫閾値を入力" />
        </Form.Item>
        <Form.Item
          name="reorderThreshold"
          label="発注点閾値"
          dependencies={['minimumThreshold']}
          rules={[
            { required: true, message: '発注点閾値を入力してください' },
            {
              type: 'integer',
              min: INVENTORY_THRESHOLD_MIN,
              message: '0以上の整数を入力してください',
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (value === undefined || value >= getFieldValue('minimumThreshold')) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('発注点閾値は最低在庫閾値以上にしてください'));
              },
            }),
          ]}
          extra="この値を下回ると「発注推奨」アラートが表示されます"
        >
          <InputNumber className="w-full" placeholder="発注点閾値を入力" />
        </Form.Item>
        <Form.Item className="mb-0 text-right">
          <Space>
            <Button onClick={onCancel}>キャンセル</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              disabled={!isSubmittable}
            >
              保存
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
}
